import { useEffect, useState } from 'react';
import { Bot, CheckCircle2, ExternalLink, LoaderCircle, RefreshCw, Send } from 'lucide-react';
import { api } from '../api/client';

function formatDate(value) {
  if (!value) return '-';
  return new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value));
}

function formatElapsed(ms) {
  const seconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return `${minutes}:${String(rest).padStart(2, '0')}`;
}

function progressLabel(progress, elapsedMs) {
  if (!progress) return '';
  const elapsedSeconds = Math.floor(elapsedMs / 1000);
  if (progress.mode === 'items') {
    if (elapsedSeconds < 10) return 'Original-Links werden besucht';
    if (elapsedSeconds < 25) return 'Inhalte und Bilder werden gescraped';
    if (elapsedSeconds < 55) return `OpenAI erzeugt ${progress.articleCount} Einzelentwuerfe`;
    return progress.publishToWordpress ? 'WordPress-Drafts werden angelegt' : 'Entwuerfe werden gespeichert';
  }
  if (elapsedSeconds < 10) return 'Collection-Inhalte werden gebuendelt';
  if (elapsedSeconds < 35) return 'OpenAI erzeugt den Sammelentwurf';
  return progress.publishToWordpress ? 'WordPress-Draft wird angelegt' : 'Entwurf wird gespeichert';
}

export default function CollectionsPage() {
  const [collections, setCollections] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [progress, setProgress] = useState(null);
  const [now, setNow] = useState(Date.now());

  async function load() {
    setLoading(true);
    setError('');
    try {
      const data = await api.collections();
      setCollections(data.collections);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!progress) return undefined;
    const interval = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [progress]);

  async function markReady(id) {
    await api.markCollectionReady(id);
    await load();
  }

  async function generate(collection, publishToWordpress) {
    setProcessingId(collection.id);
    setProgress({
      id: collection.id,
      mode: 'collection',
      publishToWordpress,
      articleCount: collection.article_count,
      startedAt: Date.now(),
    });
    setError('');
    try {
      await api.generateCollection(collection.id, publishToWordpress);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessingId(null);
      setProgress(null);
    }
  }

  async function generateItems(collection, publishToWordpress) {
    setProcessingId(collection.id);
    setProgress({
      id: collection.id,
      mode: 'items',
      publishToWordpress,
      articleCount: collection.article_count,
      startedAt: Date.now(),
    });
    setError('');
    try {
      await api.generateCollectionItems(collection.id, publishToWordpress);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessingId(null);
      setProgress(null);
    }
  }

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <span className="eyebrow">Redaktion</span>
          <h1>Collections</h1>
        </div>
        <button className="secondary-button" onClick={load} type="button">
          <RefreshCw size={16} />
          Refresh
        </button>
      </header>

      {error ? <div className="alert">{error}</div> : null}
      {loading ? <div className="screen-status">Collections laden...</div> : null}

      <section className="collection-list">
        {collections.map((collection) => (
          <article className="collection-row" key={collection.id}>
            <div>
              <span className={`status-pill ${collection.status}`}>{collection.status}</span>
              {collection.latest_output_status ? (
                <span className={`status-pill ${collection.latest_output_status}`}>{collection.latest_output_status}</span>
              ) : null}
              <h2>{collection.name}</h2>
              <p>{collection.description || 'Keine Beschreibung'}</p>
              <small>
                {collection.article_count} Artikel · {formatDate(collection.updated_at)} · {collection.created_by_email}
              </small>
              {collection.latest_output_error ? <p className="inline-error">{collection.latest_output_error}</p> : null}
              {progress?.id === collection.id ? (
                <div className="collection-progress" role="status">
                  <div className="collection-progress-header">
                    <span>
                      <LoaderCircle size={16} />
                      {progress.mode === 'items' ? 'Einzelentwuerfe laufen' : 'Sammelentwurf laeuft'}
                    </span>
                    <strong>{formatElapsed(now - progress.startedAt)}</strong>
                  </div>
                  <div className="progress-track">
                    <span />
                  </div>
                  <p>
                    {progressLabel(progress, now - progress.startedAt)}
                    {progress.publishToWordpress ? ' · WordPress aktiv' : ' · nur Source Manager'}
                  </p>
                </div>
              ) : null}
            </div>
            <div className="collection-actions">
              {collection.wordpress_edit_url ? (
                <a className="secondary-button" href={collection.wordpress_edit_url} rel="noreferrer" target="_blank">
                  <ExternalLink size={16} />
                  WordPress
                </a>
              ) : null}
              <button
                className="secondary-button"
                disabled={collection.status !== 'draft' || processingId === collection.id}
                onClick={() => markReady(collection.id)}
                type="button"
              >
                <CheckCircle2 size={16} />
                Ready
              </button>
              <button
                className="secondary-button"
                disabled={!collection.article_count || processingId === collection.id}
                onClick={() => generate(collection, false)}
                title="Ein Entwurf aus der gesamten Collection, ohne WordPress-Post"
                type="button"
              >
                <Bot size={16} />
                {processingId === collection.id ? 'Laeuft...' : 'Sammelentwurf'}
              </button>
              <button
                className="primary-button"
                disabled={!collection.article_count || processingId === collection.id}
                onClick={() => generate(collection, true)}
                title="Ein WordPress-Draft aus der gesamten Collection"
                type="button"
              >
                <Send size={16} />
                Sammelentwurf in WP
              </button>
              <button
                className="secondary-button"
                disabled={!collection.article_count || processingId === collection.id}
                onClick={() => generateItems(collection, false)}
                title="Ein separater Entwurf pro Collection-Item, ohne WordPress-Posts"
                type="button"
              >
                <Bot size={16} />
                Einzelentwürfe
              </button>
              <button
                className="primary-button"
                disabled={!collection.article_count || processingId === collection.id}
                onClick={() => generateItems(collection, true)}
                title="Ein separater WordPress-Draft pro Collection-Item"
                type="button"
              >
                <Send size={16} />
                Einzelentwürfe in WP
              </button>
            </div>
          </article>
        ))}
      </section>
    </div>
  );
}
